import React from 'react';
import {Button, Col, Drawer, Form, InputNumber, Row, Typography} from 'antd';
import {PlusOutlined} from '@ant-design/icons';

import {useGetCompanyInfoQuery} from '../../store/api/settingsApi';
import {useCreatePurchaseMutation} from '../../store/api/purchaseApi';
import EngageCurrency from '../billings/EngageCurrency';
import {formItemFullWidth} from '../../assets/styles';

import dayjs from 'dayjs';
import {CreatePurchaseParams, SharesExchangeDrawerProps} from '../../types/purchaseTypes';
import {wholeNumberGreaterOrEqualToOne} from '../../helpers/text';
import {useAppSelector} from '../../hooks/useStore';
import {selectCurrentProfile} from '../../store/slices/userSlice';
import {messageType, showMessages} from '../../helpers/message';
import {BaseError} from '../../types/apiTypes';

import './SharesDrawer.less';

const SharesExchangeDrawer = ({
                                  typeOfTransaction,
                                  sharableType,
                                  isOwner
                              }: SharesExchangeDrawerProps) => {
    const profile = useAppSelector(selectCurrentProfile);
    const mobileMode = window.innerWidth <= 768;
    const [form] = Form.useForm();

    const [drawerVisible, setDrawerVisible] = React.useState(false);
    const [numOfShares, setNumOfShares] = React.useState<number>(0);

    const {data: clientInfo, error: companyInfoError} = useGetCompanyInfoQuery();
    const [createPurchase, {isLoading, error: createPurchaseError, isSuccess}] = useCreatePurchaseMutation();

    const parValue = clientInfo ? clientInfo.share_par_value : 0;

    React.useEffect(() => {
        if (companyInfoError || createPurchaseError) {
            showMessages(((companyInfoError || createPurchaseError) as BaseError).data?.message, messageType.error);
        }
    }, [companyInfoError, createPurchaseError]);

    const closeDrawer = React.useCallback(() => {
        form.resetFields();
        setNumOfShares(0);
        setDrawerVisible(false);
    }, [form]);

    React.useEffect(() => {
        if (isSuccess) {
            showMessages(`Shares successfully ${typeOfTransaction === 'buy' ? 'purchased' : 'sold'}.`, messageType.success);
            closeDrawer();
        }
    }, [closeDrawer, isSuccess, typeOfTransaction]);

    const handleFinish = (values: { 'Number of Shares': number }) => {
        const numberOfItems = typeOfTransaction === 'sell' ? -values['Number of Shares'] : values['Number of Shares'];
        const recipientId = sharableType === 'person' ? profile?.person?.id : profile?.account?.company_id;

        const body: CreatePurchaseParams = {
            account_id: profile?.account?.id,
            recipient_type: sharableType,
            recipient_id: recipientId,
            num_of_items: numberOfItems,
            item_type: 'share',
            purchased_at: dayjs().tz().format('YYYY-MM-DD HH:mm:ssZZ'),
        };

        createPurchase(body);
    };

    return (
        <>
            {
                typeOfTransaction === 'buy' ?
                    <Button
                        className="shares-history-table-buy-btn"
                        type="primary"
                        icon={<PlusOutlined/>}
                        onClick={() => setDrawerVisible(true)}
                        disabled={!isOwner}
                    >
                        Buy Shares
                    </Button>
                    :
                    <Button
                        className="shares-history-table-sell-btn"
                        type="default"
                        onClick={() => setDrawerVisible(true)}
                        disabled={!isOwner}
                    >
                        Sell Shares
                    </Button>
            }
            <Drawer
                className="shares-drawer"
                width={mobileMode ? '100%' : '45%'}
                title={typeOfTransaction === 'buy' ? 'Buy Shares' : 'Sell Shares'}
                placement="right"
                closable={true}
                onClose={closeDrawer}
                open={drawerVisible}
            >
                <Form
                    form={form}
                    layout="vertical"
                    name="shares-buy"
                    onFinish={handleFinish}
                >
                    <Row className="full-width-row">
                        <Col>
                            <Form.Item
                                name="Number of Shares"
                                label="Number of shares: "
                                rules={[{
                                    required: true,
                                    pattern: /^[1-9]\d*$/,
                                    message: wholeNumberGreaterOrEqualToOne
                                }]}
                            >
                                <InputNumber
                                    placeholder={wholeNumberGreaterOrEqualToOne}
                                    value={numOfShares}
                                    type="number"
                                    style={formItemFullWidth}
                                    onChange={(value) => setNumOfShares(value ?? 0)}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <div className="shares-calculation-container">
                        <Row justify="space-between">
                            <Col><Typography.Text strong>Number of shares</Typography.Text></Col>
                            <Col><span>{numOfShares}</span></Col>
                        </Row>
                        <Row justify="space-between">
                            <Col><Typography.Text strong>Value per share</Typography.Text></Col>
                            <Col><EngageCurrency amount={parValue}/></Col>
                        </Row>
                        <Row justify="space-between">
                            <Col><Typography.Title level={5} className="shares-calculation-label">Total Value of
                                Shares:</Typography.Title></Col>
                            <Col>
                                <Typography.Title level={5} className="shares-calculation-total">
                                    <EngageCurrency amount={numOfShares * parValue}/>
                                </Typography.Title>
                            </Col>
                        </Row>
                    </div>
                    <Row>
                        <Button className="shares-drawer-cancel-btn" onClick={closeDrawer}>Cancel</Button>
                        <Button type="primary" htmlType="submit" loading={isLoading}>
                            {typeOfTransaction === 'buy' ? 'Buy Shares' : 'Sell Shares'}
                        </Button>
                    </Row>
                </Form>
            </Drawer>
        </>
    );
};

export default SharesExchangeDrawer;
